<template>
  <v-card elevation="2" class="mb-6">
    <v-card-title class="d-flex justify-space-between align-center">
      <div>
        <v-icon left color="primary">mdi-ferry</v-icon>
        <span class="text-h6">Top Vessels by Customer Report</span>
      </div>
      <div class="d-flex align-center">
        <v-select
          v-model="currentGroupBy"
          :items="groupByOptions"
          item-text="label"
          item-value="value"
          label="Group By"
          outlined
          dense
          class="mr-3"
          style="max-width: 200px;"
          @change="onGroupByChange"
          hide-details
        ></v-select>
        <v-btn
          color="primary"
          small
          outlined
          @click="downloadCSV"
          :loading="csvLoading"
          :disabled="csvLoading || !reportData || reportData.length === 0"
        >
          <v-icon left small>mdi-download</v-icon>
          Download CSV
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="reportData || []"
        :loading="loading"
        no-data-text="No data available"
        loading-text="Loading data..."
        :page.sync="pagination.page"
        :items-per-page.sync="pagination.itemsPerPage"
        :server-items-length="pagination.totalCount"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100]
        }"
        @update:items-per-page="onItemsPerPageChange"
        @update:page="onPageChange"
        @update:sort-by="onSortChange"
        @update:sort-desc="onSortChange"
        hide-default-footer
        class="elevation-0"
      >
        <template v-slot:item.vessel_name="{ item }">
          <v-btn
            v-if="currentGroupBy === 'vessel' || currentGroupBy === 'job_scope_vessel'"
            text
            color="primary"
            small
            @click="onVesselClick(item)"
          >
            <v-icon left small>mdi-ferry</v-icon>
            {{ item.vessel_name }}
          </v-btn>
          <span v-else>{{ item.vessel_name || 'N/A' }}</span>
        </template>

        <template v-slot:item.customer_name="{ item }">
          <v-btn
            v-if="currentGroupBy === 'customer' || currentGroupBy === 'job_scope_customer'"
            text
            color="primary"
            small
            @click="onCustomerClick(item)"
          >
            <v-icon left small>mdi-account</v-icon>
            {{ item.customer_name }}
          </v-btn>
          <span v-else>{{ item.customer_name || 'N/A' }}</span>
        </template>

        <template v-slot:item.job_scope="{ item }">
          <span>{{ item.job_scope || 'N/A' }}</span>
        </template>

        <template v-slot:item.customers_involved="{ item }">
          <div v-if="item.customers_involved && item.customers_involved.length > 0">
            <span
              v-for="(customer, idx) in item.customers_involved"
              :key="customer.id"
            >
              <v-btn
                text
                x-small
                color="primary"
                class="pa-0 ma-0"
                style="min-width: auto; text-transform: none;"
                @click="onCustomerNameClick(customer.id)"
              >
                {{ customer.name }}
              </v-btn>
              <span v-if="idx < item.customers_involved.length - 1">, </span>
            </span>
          </div>
          <span v-else class="grey--text">No customers</span>
        </template>

        <template v-slot:item.vessels_involved="{ item }">
          <div v-if="item.vessels_involved && item.vessels_involved.length > 0">
            <span
              v-for="(vessel, idx) in item.vessels_involved"
              :key="vessel.id"
            >
              <v-btn
                text
                x-small
                color="primary"
                class="pa-0 ma-0"
                style="min-width: auto; text-transform: none;"
                @click="onVesselNameClick(vessel.id)"
              >
                {{ vessel.name }}
              </v-btn>
              <span v-if="idx < item.vessels_involved.length - 1">, </span>
            </span>
          </div>
          <span v-else class="grey--text">No vessels</span>
        </template>

        <template v-slot:item.job_scopes_involved="{ item }">
          <div v-if="item.job_scopes_involved && item.job_scopes_involved.length > 0">
            <span
              v-for="(jobScope, idx) in item.job_scopes_involved"
              :key="jobScope.name"
            >
              <v-btn
                text
                x-small
                color="primary"
                class="pa-0 ma-0"
                style="min-width: auto; text-transform: none;"
                @click="onJobScopeNameClick(jobScope.name)"
              >
                {{ jobScope.name }}
              </v-btn>
              <span v-if="idx < item.job_scopes_involved.length - 1">, </span>
            </span>
          </div>
          <span v-else class="grey--text">No job scopes</span>
        </template>

        <template v-slot:item.port_calls_count="{ item }">
          <v-btn
            text
            color="primary"
            small
            @click="onPortCallsClick(item)"
          >
            <v-icon left small>mdi-anchor</v-icon>
            {{ item.port_calls_count }}
          </v-btn>
        </template>

        <template v-slot:item.vessels_count="{ item }">
          <v-chip color="success" small text-color="white">
            {{ item.vessels_count || 0 }}
          </v-chip>
        </template>

        <template v-slot:item.customers_count="{ item }">
          <v-chip 
            v-if="currentGroupBy === 'vessel'"
            color="warning" 
            small 
            text-color="white"
            @click="onCustomersClick(item)"
            style="cursor: pointer;"
          >
            {{ item.customers_count || 0 }}
          </v-chip>
          <v-chip v-else color="warning" small text-color="white">
            {{ item.customers_count || 0 }}
          </v-chip>
        </template>
      </v-data-table>

      <div class="d-flex justify-space-between align-center mt-4" v-if="pagination.totalPages > 1">
        <div class="text-caption grey--text">
          Showing {{ ((pagination.page - 1) * pagination.itemsPerPage) + 1 }} to 
          {{ Math.min(pagination.page * pagination.itemsPerPage, pagination.totalCount) }} 
          of {{ pagination.totalCount }} results
        </div>
        <v-pagination
          v-model="pagination.page"
          :length="pagination.totalPages"
          :total-visible="7"
          @input="onPageChange"
        ></v-pagination>
      </div>
    </v-card-text>

    <v-dialog
      v-model="showPortCallDetail"
      max-width="1200px"
      scrollable
      persistent
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <div>
            <v-icon left color="primary">mdi-file-document</v-icon>
            Port Call Details
          </div>
          <v-btn icon @click="showPortCallDetail = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <port-call-detail-report
            :filters="portCallDetailFilters"
            @close="showPortCallDetail = false"
          ></port-call-detail-report>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import PortCallDetailReport from './PortCallDetailReport.vue';

export default {
  name: 'TopVesselsByCustomerReport',
  components: {
    PortCallDetailReport
  },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      csvLoading: false,
      reportData: [],
      currentGroupBy: 'customer',
      groupByOptions: [
        { label: 'Vessel', value: 'vessel' },
        { label: 'Customer', value: 'customer' },
        { label: 'Job Scope', value: 'job_scope' },
        { label: 'Job Scope vs Customer', value: 'job_scope_customer' },
        { label: 'Job Scope vs Vessel', value: 'job_scope_vessel' }
      ],
      pagination: {
        page: 1,
        itemsPerPage: 25,
        totalCount: 0,
        totalPages: 1
      },
      sortBy: ['port_calls_count'],
      sortDesc: [true],
      showPortCallDetail: false,
      portCallDetailFilters: {},
      filterWatchTimeout: null
    };
  },
  computed: {
    headers() {
      return this.getHeaders();
    }
  },
  watch: {
    filters: {
      handler(newVal, oldVal) {
        // Skip if this is the initial watch trigger (oldVal is null/undefined)
        if (!oldVal) {
          return;
        }
        
        // Debounce to avoid multiple rapid calls
        if (this.filterWatchTimeout) {
          clearTimeout(this.filterWatchTimeout);
        }
        
        this.filterWatchTimeout = setTimeout(() => {
          this.pagination.page = 1;
          this.loadReportData();
        }, 300);
      },
      deep: true,
      immediate: false
    }
  },
  methods: {
    getHeaders() {
      const baseHeaders = [];
      
      if (this.currentGroupBy === 'vessel') {
        baseHeaders.push({ text: 'Vessel Name', value: 'vessel_name', align: 'start', sortable: true });
        baseHeaders.push({ text: 'Customers Involved', value: 'customers_involved', align: 'start', sortable: false });
        baseHeaders.push({ text: 'Job Scopes Involved', value: 'job_scopes_involved', align: 'start', sortable: false });
        baseHeaders.push(
          { text: 'Port Calls', value: 'port_calls_count', align: 'center', sortable: true },
          { text: 'Customers Count', value: 'customers_count', align: 'center', sortable: true }
        );
      } else if (this.currentGroupBy === 'customer') {
        baseHeaders.push({ text: 'Customer Name', value: 'customer_name', align: 'start', sortable: true });
        baseHeaders.push({ text: 'Vessels Involved', value: 'vessels_involved', align: 'start', sortable: false });
        baseHeaders.push({ text: 'Job Scopes Involved', value: 'job_scopes_involved', align: 'start', sortable: false });
        baseHeaders.push(
          { text: 'Port Calls', value: 'port_calls_count', align: 'center', sortable: true },
          { text: 'Vessels Count', value: 'vessels_count', align: 'center', sortable: true }
        );
      } else if (this.currentGroupBy === 'job_scope') {
        baseHeaders.push({ text: 'Job Scope', value: 'job_scope', align: 'start', sortable: true });
        baseHeaders.push({ text: 'Customers Involved', value: 'customers_involved', align: 'start', sortable: false });
        baseHeaders.push({ text: 'Vessels Involved', value: 'vessels_involved', align: 'start', sortable: false });
        baseHeaders.push(
          { text: 'Port Calls', value: 'port_calls_count', align: 'center', sortable: true },
          { text: 'Vessels Count', value: 'vessels_count', align: 'center', sortable: true },
          { text: 'Customers Count', value: 'customers_count', align: 'center', sortable: true }
        );
      } else if (this.currentGroupBy === 'job_scope_customer') {
        baseHeaders.push({ text: 'Job Scope', value: 'job_scope', align: 'start', sortable: true });
        baseHeaders.push({ text: 'Customer Name', value: 'customer_name', align: 'start', sortable: true });
        baseHeaders.push({ text: 'Vessels Involved', value: 'vessels_involved', align: 'start', sortable: false });
        baseHeaders.push(
          { text: 'Port Calls', value: 'port_calls_count', align: 'center', sortable: true },
          { text: 'Vessels Count', value: 'vessels_count', align: 'center', sortable: true }
        );
      } else if (this.currentGroupBy === 'job_scope_vessel') {
        baseHeaders.push({ text: 'Job Scope', value: 'job_scope', align: 'start', sortable: true });
        baseHeaders.push({ text: 'Vessel Name', value: 'vessel_name', align: 'start', sortable: true });
        baseHeaders.push({ text: 'Customers Involved', value: 'customers_involved', align: 'start', sortable: false });
        baseHeaders.push(
          { text: 'Port Calls', value: 'port_calls_count', align: 'center', sortable: true },
          { text: 'Customers Count', value: 'customers_count', align: 'center', sortable: true }
        );
      }

      return baseHeaders;
    },
    
    async loadReportData() {
      this.loading = true;
      try {
        const params = {
          group_by: this.currentGroupBy,
          page: this.pagination.page,
          per_page: this.pagination.itemsPerPage,
          sort_by: this.sortBy && this.sortBy.length > 0 ? this.sortBy[0] : 'port_calls_count',
          sort_desc: this.sortDesc && this.sortDesc.length > 0 ? this.sortDesc[0] : true
        };
        
        if (this.filters) {
          if (this.filters.vessel_id) params.vessel_id = this.filters.vessel_id;
          if (this.filters.customer_id) params.customer_id = this.filters.customer_id;
          if (this.filters.job_scope) params.job_scope = this.filters.job_scope;
          if (this.filters.start_date) params.start_date = this.filters.start_date;
          if (this.filters.end_date) params.end_date = this.filters.end_date;
        }
        
        const response = await this.$api.reports.getTopVesselsByCustomerDrilldown(params);
        const data = response.result || response;
        const reportDataArray = Array.isArray(data.report_data) ? data.report_data : [];
        
        const mappedData = reportDataArray.map(item => {
          return item;
        });
        
        this.$set(this, 'reportData', mappedData);
        this.pagination.totalCount = data.total_count || 0;
        this.pagination.totalPages = data.total_pages || 1;
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load report data: ${error.message}`,
        });
        this.reportData = [];
      } finally {
        this.loading = false;
      }
    },
    
    onGroupByChange() {
      this.pagination.page = 1;
      this.loadReportData();
    },
    
    onPageChange() {
      this.loadReportData();
    },
    
    onItemsPerPageChange(itemsPerPage) {
      this.pagination.itemsPerPage = itemsPerPage;
      this.pagination.page = 1;
      this.loadReportData();
    },
    
    onSortChange() {
      this.pagination.page = 1;
      this.loadReportData();
    },
    
    onVesselNameClick(vesselId) {
      this.$emit('filter-change', null, { vessel_id: vesselId });
    },
    
    onCustomerNameClick(customerId) {
      this.$emit('filter-change', null, { customer_id: customerId });
    },
    
    onJobScopeNameClick(jobScope) {
      this.$emit('filter-change', null, { job_scope: jobScope });
    },
    
    onVesselClick(item) {
      this.$emit('filter-change', null, { vessel_id: item.vessel_id });
      this.loadReportData();
    },
    
    onCustomerClick(item) {
      this.currentGroupBy = 'customer';
      this.$emit('filter-change', null, { customer_id: item.customer_id });
      this.loadReportData();
    },
    
    onCustomersClick(item) {
      this.currentGroupBy = 'customer';
      this.$emit('filter-change', null, { vessel_id: item.vessel_id });
      this.loadReportData();
    },
    
    onPortCallsClick(item) {
      this.portCallDetailFilters = {
        vessel_id: item.vessel_id || this.filters.vessel_id || null,
        customer_id: item.customer_id || this.filters.customer_id || null,
        job_scope: item.job_scope || this.filters.job_scope || null,
        start_date: this.filters.start_date || null,
        end_date: this.filters.end_date || null
      };
      
      Object.keys(this.portCallDetailFilters).forEach(key => {
        const value = this.portCallDetailFilters[key];
        if (value === null || value === undefined || value === '') {
          delete this.portCallDetailFilters[key];
        }
      });
      
      this.showPortCallDetail = true;
    },
    
    async downloadCSV() {
      this.csvLoading = true;
      try {
        const params = {
          group_by: this.currentGroupBy,
          download_csv: 'true'
        };
        
        if (this.filters) {
          if (this.filters.vessel_id) params.vessel_id = this.filters.vessel_id;
          if (this.filters.customer_id) params.customer_id = this.filters.customer_id;
          if (this.filters.job_scope) params.job_scope = this.filters.job_scope;
          if (this.filters.start_date) params.start_date = this.filters.start_date;
          if (this.filters.end_date) params.end_date = this.filters.end_date;
        }
        
        const queryString = Object.keys(params)
          .filter(key => params[key] != null && params[key] !== '')
          .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
          .join('&');
        
        const url = `/api/v1/reports/top_vessels_by_customer_drilldown.csv?${queryString}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'text/csv',
          },
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to download CSV: ${errorText}`);
        }
        
        const blob = await response.blob();
        const url_blob = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url_blob;
        link.setAttribute('download', `top_vessels_by_customer_report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url_blob);
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to download CSV: ${error.message}`,
        });
      } finally {
        this.csvLoading = false;
      }
    }
  },
  mounted() {
    // Load report data once after component is mounted and filters are set
    // Use nextTick to ensure parent has finished setting filters
    this.$nextTick(() => {
      if (this.filters && this.filters.start_date && this.filters.end_date) {
        this.loadReportData();
      }
    });
  },
  beforeDestroy() {
    // Clean up timeout
    if (this.filterWatchTimeout) {
      clearTimeout(this.filterWatchTimeout);
    }
  }
};
</script>

<style scoped>
.v-card {
  border-radius: 8px;
}
</style>
